import React, { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { absUrl } from "../lib/api";

/** Gallery card that shows a cover image with a "+N" badge if multiple images exist.
 *  Clicking opens a lightbox that paginates through all images. */
export default function MultiImageCard({ item, children, testId }) {
  const [open, setOpen] = useState(false);
  const images = item.images && item.images.length > 0 ? item.images : (item.image_url ? [{ url: item.image_url }] : []);
  if (!images.length) return null;

  return (
    <>
      <button onClick={() => setOpen(true)} className="text-left brand-surface rounded-xl overflow-hidden hover-lift w-full" data-testid={testId}>
        <div className="relative">
          <img src={absUrl(images[0].url)} alt="" className="w-full h-44 object-cover" />
          {images.length > 1 && (
            <span className="absolute top-2 right-2 bg-black/75 text-[11px] font-bold px-2 py-0.5 rounded border border-white/10 text-white">+{images.length - 1}</span>
          )}
          {item.inspection_type && (
            <span className={`absolute top-2 left-2 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${item.inspection_type === "pre" ? "bg-amber-500/15 text-amber-400 border border-amber-500/30" : "bg-green-500/15 text-green-400 border border-green-500/30"}`}>{item.inspection_type}</span>
          )}
        </div>
        <div className="p-3">{children}</div>
      </button>
      <Lightbox open={open} onClose={() => setOpen(false)} images={images} item={item} />
    </>
  );
}

export function Lightbox({ open, onClose, images, item }) {
  const [idx, setIdx] = useState(0);
  React.useEffect(() => { if (open) setIdx(0); }, [open]);
  if (!images || !images.length) return null;
  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-5xl text-white p-0 overflow-hidden" data-testid="lightbox-dialog">
        <div className="relative bg-black">
          <img src={absUrl(images[idx].url)} alt="" className="w-full max-h-[75vh] object-contain" />
          {images.length > 1 && (
            <>
              <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#FF3B30] border border-white/10 rounded-full p-2" data-testid="lightbox-prev"><ChevronLeft /></button>
              <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-[#FF3B30] border border-white/10 rounded-full p-2" data-testid="lightbox-next"><ChevronRight /></button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 border border-white/10 rounded-full px-3 py-1 text-xs font-mono">{idx + 1} / {images.length}</div>
            </>
          )}
        </div>
        <div className="p-5">
          {item.dealership_name && <div className="text-[#FF3B30] font-semibold">{item.dealership_name}</div>}
          {item.customer_name && <div className="text-sm">{item.customer_name}{item.inspection_type && <span className="ml-2 text-[#FF3B30] uppercase font-semibold text-xs">{item.inspection_type}</span>}</div>}
          {item.user_name && <div className="text-xs text-zinc-400 mt-0.5">Posted by {item.user_name}</div>}
          {item.notes && <div className="text-sm text-zinc-300 mt-2">{item.notes}</div>}
          {item.address && <div className="text-[11px] text-zinc-500 mt-1 truncate">📍 {item.address}</div>}
          {item.created_at && <div className="text-xs text-zinc-500 mt-1 font-mono">{new Date(item.created_at).toLocaleString()}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
